import { getFilters, buildDeliverySlotTimeFilter } from 'components/consignments/consignments.helper';
import moment from 'moment';
import { Dispatch } from 'redux';
import { ConsignmentBuckets } from '../components/consignments/consignments.constants';
import { Buckets } from '../library/Types';
import {
    fetchConsignments,
    fetchSummary,
    fetchDetailsColumns as fetchDetailsColumnsAPI,
    updateDetailsColumns as updateDetailsColumnsAPI,
} from '../network/consignments.api';
import { ReduxStore } from '../reducers/redux.types';
import {
    endLoading,
    startLoading,
    setGenericData,
    setGenericSummary,
    setPagination,
} from './generic-action';
import { PAGE_NAMES } from 'types/page.types';
import { fetchObjectViewDataObjects } from 'network/objectview.api';
import { message } from 'antd';
import { FETCH_DETAILS_COLUMNS_LOADING, SET_DETAILS_COLUMNS } from 'reducers/redux-constants';

const bucket = Buckets.CONSIGNMENTS;
const dateFormat = 'YYYY-MM-DD';

// const independentFilters = [
//     'sortBy',
//     'endDate',
//     'startDate',
//     'bucketSelected',
//     'descendingOrder',
//     'isInternational',
//     'consignmentType',
//     'cncFlag',
// ];

const arrayFilters = ['statusList', 'bookingServiceTypeIdList', 'customerReferenceNumberList', 'referenceNumberList'];
export const getMoreFilters = (appliedFilters: any) => {
    const filters: Record<string, any> = {};
    Object.keys(appliedFilters).forEach((filter) => {
        filters[filter] = appliedFilters[filter];
        if (filter === 'cncFlag' && appliedFilters.cncFlag && appliedFilters.cncFlag !== 'all') {
            filters[filter] = appliedFilters.cncFlag === 'true';
        }
        if (arrayFilters.includes(filter)) {
            const filterValue = appliedFilters[filter];
            if (['customerReferenceNumberList', 'referenceNumberList', 'statusList'].includes(filter)) {
                if (Array.isArray(filterValue)) {
                    filters[filter] = filterValue;
                } else if (typeof filterValue === 'string') {
                    filters[filter] = filterValue.split(/[, ]+/).filter(Boolean);
                } else {
                    filters[filter] = [];
                }
            } else if (Array.isArray(filterValue)) {
                filters[filter] = filterValue;
            } else if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
                filters[filter] = [filterValue];
            } else {
                filters[filter] = [];
            }
        }
    });
    return filters;
};

const loadSummary = (
    appliedFilters: any, filters: any, timeFilters?: any[], childAccountsList?: any[],
) => async (dispatch: Dispatch<any>) => {
    let normalizedCustomerIdList: any[] = [];
    if (Array.isArray(filters.customerIdList)) {
        normalizedCustomerIdList = filters.customerIdList;
    } else if (typeof filters.customerIdList === 'string' && filters.customerIdList.length > 0) {
        normalizedCustomerIdList = [filters.customerIdList];
    }
    const resolvedCustomerIdList = normalizedCustomerIdList.length
        ? normalizedCustomerIdList
        : (childAccountsList || []).map((child: any) => child.customer_id);

    const response = await fetchSummary({
        ...filters,
        dateFilterColumn: filters.dateType || 'created_at',
        customerIdList: resolvedCustomerIdList,
        bucketIds: Object.keys(ConsignmentBuckets),
        startDate: moment(appliedFilters.startDate).format('YYYY-MM-DD'),
        endDate: moment(appliedFilters.endDate).format('YYYY-MM-DD'),
        ...(timeFilters ? { timeFilters } : {}),
    });
    if (response.isSuccess) {
        dispatch(setGenericSummary(bucket, response?.data || {}));
    } else {
        dispatch(setGenericSummary(bucket, {}));
    }
};

export const loadConsignments = () => async (
    dispatch: Dispatch<any>,
    store: () => ReduxStore,
) => {
    dispatch(startLoading(bucket));

    const { generic, master, genericPage } = store();
    const { CONSIGNMENTS } = generic;
    const { config } = master;
    const { appliedFilters, pagination } = CONSIGNMENTS;
    const consignmentPageData = genericPage[PAGE_NAMES.CONSIGNMENTS];
    const isPageBuilderData = !!consignmentPageData;
    const filtersInfo = isPageBuilderData ? [] : getFilters(appliedFilters.bucketSelected, config);
    const rawFilters = getMoreFilters(appliedFilters);
    filtersInfo.forEach((filter: any) => {
        if (rawFilters[filter.id] && filter.mode === 'multiple' && typeof rawFilters[filter.id] === 'string') {
            rawFilters[filter.id] = rawFilters[filter.id].split(',').map((value: string) => value.trim());
        }
    });
    const deliverySlotValue = rawFilters.delivery_time_slot || rawFilters.deliveryTimeslot;
    const filters = { ...rawFilters };
    delete filters.delivery_time_slot;
    delete filters.deliveryTimeslot;

    const baseTimeFilters = [
        {
            columnName: appliedFilters.dateType || appliedFilters.sortBy,
            fromDateString: moment(appliedFilters.startDate).format(dateFormat),
            toDateString: moment(appliedFilters.endDate).format(dateFormat),
        },
    ];
    const deliverySlotTimeFilter = buildDeliverySlotTimeFilter(deliverySlotValue);
    const combinedTimeFilters = deliverySlotTimeFilter ? [...baseTimeFilters, deliverySlotTimeFilter] : baseTimeFilters;

    let normalizedCustomerIdList: any[] = [];
    if (Array.isArray(filters.customerIdList)) {
        normalizedCustomerIdList = filters.customerIdList;
    } else if (typeof filters.customerIdList === 'string' && filters.customerIdList.length > 0) {
        normalizedCustomerIdList = [filters.customerIdList];
    }
    const resolvedCustomerIdList = normalizedCustomerIdList.length
        ? normalizedCustomerIdList
        : (master?.children_list || []).map((child: any) => child.customer_id);

    if (isPageBuilderData) {
        // sortBy: appliedFilters.dateType || appliedFilters.sortBy,
        // descendingOrder: appliedFilters.descendingOrder,
        const body: any = {
            viewId: appliedFilters.bucketSelected,
            filters: {
                ...filters,
                customerIdList: resolvedCustomerIdList,
                timeFilters: combinedTimeFilters,
            },
            pagination: {
                resultPerPage: pagination.resultPerPage,
                pageNumber: pagination.currentPageNumber,
            },
        };
        delete body.filters.bucketSelected;
        Object.keys(body.filters).forEach((key) => {
            if (!body.filters[key]) {
                delete body.filters[key];
            }
        });
        const response = await fetchObjectViewDataObjects(body);
        if (response.isSuccess) {
            dispatch(setGenericData(bucket, response?.data?.page_data || []));
            dispatch(setPagination(bucket, {
                isNextPresent: response.data?.isNextPresent,
            }));
        } else {
            dispatch(setGenericData(bucket, []));
        }
    } else {
        dispatch(loadSummary(appliedFilters, filters, combinedTimeFilters, master?.children_list));
        const response = await fetchConsignments({
            ...filters,
            resultPerPage: pagination.resultPerPage,
            pageNumber: pagination.currentPageNumber,
            customerIdList: resolvedCustomerIdList,
            bucket: appliedFilters.bucketSelected,
            timeFilters: combinedTimeFilters,
            descendingOrder: appliedFilters.descendingOrder,
            startDate: moment(appliedFilters.startDate).format(dateFormat),
            endDate: moment(appliedFilters.endDate).format(dateFormat),
            sortBy: appliedFilters.sortBy || appliedFilters.dateType,
        });
        if (response.isSuccess) {
            dispatch(setGenericData(bucket, response?.data?.page_data || []));
            dispatch(setPagination(bucket, {
                isNextPresent: response.data?.isNextPresent,
            }));
        } else {
            dispatch(setGenericData(bucket, []));
        }
    }

    dispatch(endLoading(bucket));
};

export const fetchDetailsColumns = () => async (dispatch: Dispatch<any>, store: () => ReduxStore) => {
    const { generic } = store();

    // Check if data already exists and is not empty
    if (
        generic.CONSIGNMENTS?.detailsColumns?.selectedColumns
        && generic.CONSIGNMENTS.detailsColumns.selectedColumns.length > 0
    ) {
        return;
    }

    dispatch({ type: FETCH_DETAILS_COLUMNS_LOADING, bucketId: bucket, data: true });

    const response = await fetchDetailsColumnsAPI();

    if (response.isSuccess) {
        dispatch({
            type: SET_DETAILS_COLUMNS,
            bucketId: bucket,
            data: {
                selectedColumns: response.data?.consignment_details_column_list || [],
            },
        });
    } else {
        message.error(response.errorMessage || 'Failed to fetch column details');
    }

    dispatch({ type: FETCH_DETAILS_COLUMNS_LOADING, bucketId: bucket, data: false });
};

interface GroupedColumnDetail {
    column_id?: string;
    header?: string;
    columns?: Array<{
        pretty_name: string;
        id: string;
        key: string;
        type: string;
    }>;
    pretty_name?: string;
    id?: string;
    key?: string;
    type?: string;
}

interface GroupedColumnData {
    tab_header: string;
    details: GroupedColumnDetail[];
}

export const updateDetailsColumns = (columns: GroupedColumnData[]) => async (dispatch: Dispatch<any>) => {
    const response = await updateDetailsColumnsAPI({
        consignment_details_column_list: columns,
    });

    if (response.isSuccess) {
        dispatch({
            type: SET_DETAILS_COLUMNS,
            bucketId: bucket,
            data: { selectedColumns: response.data?.consignment_details_column_list || [] },
        });
        message.success('Column preferences updated successfully');
    } else {
        message.error(response.errorMessage || 'Failed to update column preferences');
        throw new Error(response.errorMessage);
    }
};
